import { useEffect, useCallback, useRef, useMemo, useState } from "react";
import { Formik, Form, useFormikContext } from "formik";
import { Spinner } from "@chakra-ui/react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
	getPastKeyPersonnelForRfx,
} from "actions/RFQ/Supplier/rfqActions";
import { showToast } from "utils/helpers";
import apiV2 from "api-v2";
import { SmartFieldCancelModal } from "./_components/SmartFieldCancelModal";
import { useSmartFieldEditor } from "../SmartFieldEditor/hooks/useSmartFieldEditor";
import {
	LoadingView,
	ErrorView,
	NoDataView,
} from "../SmartFieldEditor/_components/SmartFieldStatusViews";
import { Button } from "newui/components/Button";
import { KeyPersonnelFullForm } from "newui/features/Company/KeyPersonnel/components/KeyPersonnelFullForm";
import { createKeyPersonnelValidation } from "newui/features/Company/KeyPersonnel/validation/keyPersonnelValidation";
import { buildFormData } from "newui/features/Company/KeyPersonnel/key-personnel-api";

const validationSchema = createKeyPersonnelValidation(2);

/** Keeps an external ref in sync with Formik's live values */
const FormikValuesSync = ({ valuesRef }) => {
	const { values } = useFormikContext();
	valuesRef.current = values;
	return null;
};

/**
 * Map the API detail response to KeyPersonnelFormValues shape
 * expected by KeyPersonnelFullForm.
 */
const mapDetailToFormValues = (detail) => ({
	first_name: detail.first_name || "",
	last_name: detail.last_name || "",
	email: detail.email || "",
	position: detail.position || "",
	experience: detail.experience?.toString() || "",
	sort_order: detail.sort_order ?? null,
	sector_ids: Array.isArray(detail.sectors)
		? detail.sectors.map((s) => s.id)
		: [],
	disciplines: detail.disciplinesFormatted?.length
		? detail.disciplinesFormatted
		: [],
	avatar_image: detail.avatar_location || null,
	projects_completed: Array.isArray(detail.projects_completed)
		? detail.projects_completed
		: detail.projects_completed?.data ?? [],
	resume_files: (detail.resume || []).map((r) => ({
		id: r.id,
		is_default: r.is_default,
	})),
});

/**
 * Build FormData for the RFx replacement endpoint.
 * Uses the shared buildFormData utility for consistent field handling,
 * then appends rfq_id for the replacement context.
 */
const buildReplacementFormData = (values, rfxId) => {
	const fd = buildFormData(values);
	fd.append("rfq_id", String(rfxId));
	return fd;
};

export const SmartFieldKeyPersonnelEditor = ({
	keyPersonnelId,
	rfxId,
	companyId,
	onRequestClose,
	...props
}) => {
	const [isSaving, setIsSaving] = useState(false);
	const {
		isModalOpen,
		isModalSaving,
		setIsModalSaving,
		isInModalFlow,
		setIsInModalFlow,
		errorStatus,
		setErrorStatus,
		handleCancelClick,
		handleModalClose,
	} = useSmartFieldEditor({
		id: keyPersonnelId,
		rfxId,
		companyId,
		entityType: "KeyPersonnel",
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const currentValuesRef = useRef(null);
	const queryClient = useQueryClient();

	const {
		data: keyPersonnelData,
		isLoading,
		error: keyPersonnelError,
	} = useQuery({
		queryKey: ["pastKeyPersonnelForRfx", { keyPersonnelId, rfqId: rfxId }],
		queryFn: getPastKeyPersonnelForRfx,
		enabled: !!keyPersonnelId && !!rfxId,
	});

	const initialValues = useMemo(() => {
		if (!keyPersonnelData) {
			return null;
		}
		return mapDetailToFormValues(keyPersonnelData);
	}, [keyPersonnelData]);

	useEffect(() => {
		if (initialValues) {
			currentValuesRef.current = initialValues;
		}
	}, [initialValues]);

	const invalidateKeyPersonnelQuery = useCallback(() => {
		queryClient.invalidateQueries([
			"pastKeyPersonnelForRfx",
			{ keyPersonnelId, rfqId: rfxId },
		]);
	}, [queryClient, keyPersonnelId, rfxId]);

	const makeDirectApiCall = useCallback(
		async (values) => {
			setIsSaving(true);
			try {
				const formData = buildReplacementFormData(values, rfxId);
				const response = await apiV2.post(
					`/key-personnel/${keyPersonnelId}/save-replacement`,
					formData
				);
				invalidateKeyPersonnelQuery();
				queryClient.invalidateQueries({ queryKey: ["key-personnel"] });
				showToast(
					response.data?.message || "Key personnel saved successfully",
					"Success",
					true
				);
				return response.data;
			} catch (err) {
				showToast(
					err?.response?.data?.message || "Failed to save key personnel changes",
					"Error"
				);
				if (err.response?.data) {
					throw err.response.data;
				}
				throw err;
			} finally {
				setIsSaving(false);
				setIsModalSaving(false);
			}
		},
		[
			keyPersonnelId,
			rfxId,
			setIsSaving,
			setIsModalSaving,
			invalidateKeyPersonnelQuery,
			queryClient,
		]
	);

	const handleSaveButtonClick = useCallback(() => {
		setIsSaving(true);
		const values = currentValuesRef.current || initialValues;
		makeDirectApiCall(values)
			.then(() => {
				if (onRequestClose) {
					onRequestClose();
				}
			})
			.catch(() => {
				setIsSaving(false);
			});
	}, [makeDirectApiCall, initialValues, setIsSaving, onRequestClose]);

	const handleSaveFromModal = useCallback(() => {
		setIsModalSaving(true);
		const values = currentValuesRef.current || initialValues;
		makeDirectApiCall(values)
			.then(() => {
				if (onRequestClose) {
					onRequestClose();
				}
			})
			.catch(() => {
				setIsModalSaving(false);
			})
			.finally(() => {
				setIsInModalFlow(false);
			});
	}, [
		makeDirectApiCall,
		initialValues,
		setIsModalSaving,
		setIsInModalFlow,
		onRequestClose,
	]);

	const handleSubmit = useCallback(
		async (values, { setSubmitting, setErrors, setStatus }) => {
			currentValuesRef.current = values;

			if (!isInModalFlow || isModalSaving) {
				try {
					setIsSubmitting(true);
					await makeDirectApiCall(values);
				} catch (error) {
					if (error.errors) {
						setErrors(error.errors);
					}
					setStatus("Error");
					setErrorStatus(true);
				} finally {
					setIsSubmitting(false);
				}
			}

			if (!isModalOpen) {
				setIsInModalFlow(false);
			}

			setSubmitting(false);
		},
		[
			makeDirectApiCall,
			isInModalFlow,
			isModalSaving,
			isModalOpen,
			setErrorStatus,
			setIsInModalFlow,
		]
	);

	if (isLoading) {
		return <LoadingView entityType="key personnel" />;
	}

	if (keyPersonnelError) {
		return (
			<ErrorView
				error={keyPersonnelError}
				entityType="key personnel"
			/>
		);
	}

	if (!initialValues) {
		return <NoDataView entityType="key personnel" />;
	}

	return (
		<div className="p-4">
			<Formik
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={handleSubmit}
				enableReinitialize
			>
				{({ dirty }) => (
					<Form>
						<FormikValuesSync valuesRef={currentValuesRef} />
						<KeyPersonnelFullForm
							profileTypeId={2}
							existingResumes={keyPersonnelData?.resume ?? []}
						/>

						<div className="mt-6 flex justify-end gap-4">
							<Button
								btntype="link"
								disabled={isSaving}
								onClick={handleCancelClick}
							>
								Cancel
							</Button>
							<Button
								type="button"
								isLoading={isSaving}
								disabled={isSaving}
								onClick={handleSaveButtonClick}
							>
								Save Changes to RFX
							</Button>
						</div>
					</Form>
				)}
			</Formik>
			<SmartFieldCancelModal
				isOpen={isModalOpen}
				onClose={handleModalClose}
				onDiscard={() => onRequestClose()}
				onSave={handleSaveFromModal}
				isSaving={isModalSaving}
			/>
		</div>
	);
};
